import {Box, Hide, HStack, Show, Text, useDevice} from 'platform/foundation';
import {useFormatPercentage} from 'platform/locale';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {AvailableCar} from '../../../types/AvailableCar';
import {PriceReportType} from '../../../types/PriceReportType';
import {getDifference} from '../../../utils/getDifference';
import {DEFAULT_VALUE} from '../consts/defaultValue';
import {HorizontalValue, HorizontalValueProps} from './HorizontalValue';
import {VerticalValue, VerticalValueProps} from './VerticalValue';

interface VehicleCardContentFeatureScoreProps extends TestIdProps {
  vehicle: AvailableCar;
  priceReport: PriceReportType;
  isMyVehicle: boolean;
}

export function VehicleCardContentFeatureScore(props: VehicleCardContentFeatureScoreProps) {
  const formatPercentage = useFormatPercentage();
  const isMobile = useDevice() === 'mobile';

  const featuresDifference = getDifference(
    props.vehicle.feature_score,
    props.priceReport?.featuresLevel,
    100
  );

  const valueProps: HorizontalValueProps | VerticalValueProps = {
    value: props.vehicle.feature_score,
    difference: props.isMyVehicle ? undefined : featuresDifference,
    formatter: (value) => (isNotNil(value) ? formatPercentage(value, 'down') : DEFAULT_VALUE),
    differenceFormatter: (value) =>
      // Feature difference is already rounded – formatting has to be the nearest due to rounding problems (0.29 * 100 = 28.999999999999996)
      isNotNil(value) ? formatPercentage(value, 'nearest') : DEFAULT_VALUE,
    withRank: true,
    isRow: !isMobile,
    isMyVehicle: props.isMyVehicle,
    'data-testid': suffixTestId('featureScore', props),
  };

  return (
    <>
      <Hide onMobile>
        <Box flex={1}>
          <VerticalValue {...valueProps} />
        </Box>
      </Hide>

      <Show onMobile>
        <HStack justify="space-between" align="center">
          <Text size="xSmall" color="secondary">
            {i18n.t('entity.table.featureLevel')}
          </Text>
          <HorizontalValue {...valueProps} />
        </HStack>
      </Show>
    </>
  );
}
